/**
 * WaveSignal Component
 * 
 * Displays animated wave ripples around game elements to indicate valid actions.
 * - AI_BEST: 6 waves, colored based on card color (more prominent)
 * - VALID: 3 waves, gold color (standard)
 * 
 * Supports different shapes:
 * - CIRCLE: Circular waves from center (for buttons, avatars, towers)
 * - SEMI_TOP: Half-rectangle waves from top edge (for card top effects)
 * - SEMI_BOTTOM: Half-rectangle waves from bottom edge (for card bottom effects)
 * - RECTANGLE: Full rectangle waves (for buttons)
 */

import React from 'react';
import { CardColor } from '../types';

export type WaveType = 'AI_BEST' | 'VALID' | 'GOLD' | 'STANDARD';
export type WaveShape = 'CIRCLE' | 'SEMI_TOP' | 'SEMI_BOTTOM' | 'RECTANGLE';

interface WaveSignalProps {
    type: WaveType;
    shape?: WaveShape;
    cardColor?: CardColor;  // For AI_BEST coloring
    size?: string;          // Optional custom size
    disabled?: boolean;     // Disable animation
}

// Color mapping for card colors
const CARD_COLOR_MAP: Record<CardColor, { border: string; shadow: string; glow: string }> = {
    [CardColor.Red]: {
        border: 'border-red-400',
        shadow: 'shadow-[0_0_15px_rgba(248,113,113,0.7)]',
        glow: 'rgba(248,113,113,0.7)'
    },
    [CardColor.Green]: {
        border: 'border-emerald-400',
        shadow: 'shadow-[0_0_15px_rgba(52,211,153,0.7)]',
        glow: 'rgba(52,211,153,0.7)'
    },
    [CardColor.Black]: {
        border: 'border-slate-400',
        shadow: 'shadow-[0_0_15px_rgba(148,163,184,0.7)]',
        glow: 'rgba(148,163,184,0.7)'
    },
    [CardColor.Purple]: {
        border: 'border-purple-400',
        shadow: 'shadow-[0_0_15px_rgba(192,132,252,0.7)]',
        glow: 'rgba(192,132,252,0.7)'
    },
    [CardColor.Yellow]: {
        border: 'border-yellow-400',
        shadow: 'shadow-[0_0_15px_rgba(250,204,21,0.7)]',
        glow: 'rgba(250,204,21,0.7)'
    },
    [CardColor.Pink]: {
        border: 'border-pink-400',
        shadow: 'shadow-[0_0_15px_rgba(244,114,182,0.7)]',
        glow: 'rgba(244,114,182,0.7)'
    },
};

// Gold color for VALID hints
const GOLD_COLOR = {
    border: 'border-yellow-400',
    shadow: 'shadow-[0_0_10px_rgba(250,204,21,0.6)]',
    glow: 'rgba(250,204,21,0.6)'
};

const WaveSignal: React.FC<WaveSignalProps> = ({
    type,
    shape = 'CIRCLE',
    cardColor,
    disabled = false
}) => {
    if (disabled) return null;

    // Normalize type (GOLD -> VALID, STANDARD -> VALID for backward compatibility)
    const normalizedType = (type === 'GOLD' || type === 'STANDARD') ? 'VALID' : type;

    // Config based on type
    const isAIBest = normalizedType === 'AI_BEST';
    const rippleCount = isAIBest ? 6 : 3;
    const animationDuration = isAIBest ? 4.0 : 2.5; // Slower speed for AI_BEST

    // Get colors
    const colors = isAIBest && cardColor ? CARD_COLOR_MAP[cardColor] : GOLD_COLOR;

    // Shape styles
    const getShapeClass = (): string => {
        switch (shape) {
            case 'CIRCLE':
                return 'rounded-full inset-0';
            case 'SEMI_TOP':
                return 'top-0 left-0 right-0 h-1/2 rounded-t-xl border-b-0';
            case 'SEMI_BOTTOM':
                return 'bottom-0 left-0 right-0 h-1/2 rounded-b-xl border-t-0';
            case 'RECTANGLE':
                return 'inset-0 rounded-lg';
            default:
                return 'rounded-full inset-0';
        }
    };

    // Clip path for semi-shapes to hide middle line and glow leakage
    const getClipPath = (): string | undefined => {
        if (shape === 'SEMI_TOP') return 'inset(-100% -100% 0 -100%)';
        if (shape === 'SEMI_BOTTOM') return 'inset(0 -100% -100% -100%)';
        return undefined;
    };

    // Animation keyframes name
    const animationName = isAIBest ? 'waveExpandAIBest' : 'waveExpandValid';

    return (
        <div className="absolute inset-0 pointer-events-none overflow-visible z-0">
            {/* CSS Keyframes injection */}
            <style>{`
        @keyframes waveExpandAIBest {
          0% {
            transform: scale(0.95);
            opacity: 0.9;
            border-width: 3px;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            transform: scale(1.62);
            opacity: 0;
            border-width: 1px;
          }
        }

        @keyframes waveExpandAIBestFirst {
          0% {
            transform: scale(0.95);
            opacity: 1.0;
            border-width: 6px;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            transform: scale(1.62);
            opacity: 0;
            border-width: 2px;
          }
        }
        
        @keyframes waveExpandValid {
          0% {
            transform: scale(0.98);
            opacity: 0.7;
            border-width: 2px;
          }
          50% {
            opacity: 0.35;
          }
          100% {
            transform: scale(1.47);
            opacity: 0;
            border-width: 1px;
          }
        }
      `}</style>

            {Array.from({ length: rippleCount }).map((_, i) => {
                const isFirstWave = i === 0 && isAIBest;
                const currentAnim = isFirstWave ? 'waveExpandAIBestFirst' : animationName;

                return (
                    <div
                        key={i}
                        className={`absolute border-2 ${getShapeClass()}`}
                        style={{
                            borderColor: colors.glow, // Use glow color for border
                            animation: `${currentAnim} ${animationDuration}s ease-out infinite`,
                            animationDelay: `${i * (isAIBest ? 0.65 : 0.8)}s`,
                            boxShadow: isAIBest ? `0 0 ${isFirstWave ? '25px' : '15px'} ${colors.glow}` : `0 0 10px ${colors.glow}`,
                            clipPath: getClipPath()
                        }}
                    />
                );
            })}
        </div>
    );
};

export default WaveSignal;

// ============================================================================
// CONVENIENCE WRAPPER COMPONENTS
// ============================================================================

interface CardWaveSignalProps {
    isAIBest: boolean;
    cardColor?: CardColor;
    topValid?: boolean;
    bottomValid?: boolean;
    rotation?: number;
    disabled?: boolean;
}

/**
 * Specialized wave signal for cards with top/bottom effect indicators
 */
export const CardWaveSignal: React.FC<CardWaveSignalProps> = ({
    isAIBest,
    cardColor,
    topValid = false,
    bottomValid = false,
    rotation = 0,
    disabled = false
}) => {
    if (disabled || (!topValid && !bottomValid)) return null;

    // Normalize rotation to nearest 180 (0 or 180)
    const normalizedRotation = Math.round(rotation / 180) * 180;

    return (
        <div style={{ transform: `rotate(${normalizedRotation}deg)`, transition: 'transform 0.3s' }} className="absolute inset-0">
            {topValid && (
                <WaveSignal
                    type={isAIBest ? 'AI_BEST' : 'VALID'}
                    shape="SEMI_TOP"
                    cardColor={isAIBest ? cardColor : undefined}
                />
            )}
            {bottomValid && (
                <WaveSignal
                    type={isAIBest ? 'AI_BEST' : 'VALID'}
                    shape="SEMI_BOTTOM"
                    cardColor={isAIBest ? cardColor : undefined}
                />
            )}
        </div>
    );
};

interface ElementWaveSignalProps {
    isAIBest: boolean;
    cardColor?: CardColor;
    elementType?: string;
    shape?: WaveShape;
    disabled?: boolean;
}

/**
 * General purpose wave signal for non-card elements
 */
export const ElementWaveSignal: React.FC<ElementWaveSignalProps> = ({
    isAIBest,
    cardColor,
    elementType: _elementType,
    shape = 'CIRCLE',
    disabled = false
}) => {
    if (disabled) return null;

    return (
        <WaveSignal
            type={isAIBest ? 'AI_BEST' : 'VALID'}
            shape={shape}
            cardColor={isAIBest ? cardColor : undefined}
        />
    );
};
